import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, NumericInput, ProgressBar, Callout, Alert } from '@blueprintjs/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripVertical, faXmark } from '@fortawesome/free-solid-svg-icons'
import { useLocalStorage } from 'usehooks-ts'

import { Colors } from 'design/colors'
import { useDevices } from 'hooks/useDevices'
import { useThrottleConfig, getPositionUnitLabel } from 'hooks/useThrottleConfig'
import { PositionResolution, serializeSetUsbThrottleControlRequest } from 'utils/common-types'
import { hidSend } from 'connections/hid'
import {
  CycleTestState,
  CycleTestConfig,
  CycleLogEntry,
  DEFAULT_CONFIG,
  POLL_INTERVAL_MS
} from './AutomatedCycleTesting.types'
import { downloadData, filenameWithTimestamp } from 'utils/common'
import { proxy } from 'comlink'
import { ExportType } from 'utils/hid-export'
import { packetThreadPoolApi, realtimeMetadata } from 'utils/packet-workers'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions } from 'setup/firebase'
import { PlotKey } from 'utils/packet.worker'
import { useSingleTelemetryValue } from 'hooks/useSingleTelemetryValue'

const DEFAULT_DEADBAND_MRAD = 5

export interface ICycleTestingProps {
  close: () => void
  actuatorIndex: number
  label?: string
}

// TODO: most of the hardware logic (repeated automated commands, deadband checks, etc.) should ideally live in firmware, built like Autotune or Sys ID.
// due to time constraints, this design will be utilized until that transfer can be made.
export const CycleTesting = (props: ICycleTestingProps) => {
  const { close, actuatorIndex, label } = props
  const { connectedDevice } = useDevices()

  const storageKey = `cycleTestConfig_${actuatorIndex}`
  const [config, setConfig] = useLocalStorage<CycleTestConfig>(storageKey, DEFAULT_CONFIG)
  const [deadbandMrad, setDeadbandMrad] = useLocalStorage<number>(
    `${storageKey}_deadband`,
    DEFAULT_DEADBAND_MRAD
  )

  const positionKey = (
    actuatorIndex !== undefined
      ? `MultiMotor_N${actuatorIndex}_positionUserUnits`
      : 'motorPosition'
  ) as PlotKey
  const position = useSingleTelemetryValue(positionKey) as number | undefined
  const positionRef = useRef<number | undefined>(position)
  positionRef.current = position

  const [state, setState] = useState<CycleTestState>(CycleTestState.IDLE)
  const [currentCycle, setCurrentCycle] = useState<number>(0)
  const [targetPosition, setTargetPosition] = useState<number>(0)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [cycleLog, setCycleLog] = useState<CycleLogEntry[]>([])
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [elapsedMs, setElapsedMs] = useState<number>(0)
  const [averageCycleMs, setAverageCycleMs] = useState<number>(0)
  const [showDownloadedAlert, setShowDownloadedAlert] = useState(false)
  const [downloadedFileName, setDownloadedFileName] = useState<string>('')
  const [autoExported, setAutoExported] = useState(false)
  const [errorType, setErrorType] = useState<string>('')

  const stateRef = useRef<CycleTestState>(CycleTestState.IDLE)
  const dwellStartRef = useRef<number>(0)
  const moveStartRef = useRef<number>(0)
  const previousStateRef = useRef<CycleTestState>(CycleTestState.IDLE)
  const lastPositionRef = useRef<number | null>(null)
  const lastPositionChangeTimeRef = useRef<number>(0)

  // Soft-stop / unit context: positions and deadband are now in user units
  // (mm / deg / rad / in) since the firmware reports
  // PositionSensor_ConvertToUserUnits output. The slider's commandable
  // window is [positionControlMinPos, positionControlMaxPos] (raw IP values
  // ÷ 10^positionResolution in user units), set by the soft-stop %.
  const { throttleConfig } = useThrottleConfig()
  const unitLabel = getPositionUnitLabel() || 'user units'
  // The wire format for SET_USB_THROTTLE_CONTROL is user-units × 10^resolution
  // (matches Position.tsx). Cycle-test inputs are in plain user units, so we
  // must multiply by `resolutionFactor` before serializing — otherwise the
  // command is off by 10/100/1000× and the actuator moves to a tiny fraction
  // of the typed target, never satisfying the deadband check.
  const resolutionFactor = (() => {
    if (!throttleConfig || throttleConfig.invalidReason !== undefined) return 1
    const resolution =
      throttleConfig.positionResolution === PositionResolution.UNKNOWN
        ? 0
        : throttleConfig.positionResolution
    return Math.pow(10, resolution)
  })()
  const softStopMin =
    throttleConfig && throttleConfig.invalidReason === undefined
      ? throttleConfig.positionControlMinPos / resolutionFactor
      : undefined
  const softStopMax =
    throttleConfig && throttleConfig.invalidReason === undefined
      ? throttleConfig.positionControlMaxPos / resolutionFactor
      : undefined

  const validationErrors = useMemo(() => {
    const errors: string[] = []
    if (Math.abs(config.position1 - config.position0) <= deadbandMrad) {
      errors.push(`|Position 0 - Position 1| must exceed deadband (${deadbandMrad} ${unitLabel})`)
    }
    if (config.cycleCount < 1) errors.push('Cycle count must be at least 1')
    if (config.timeoutSeconds <= 0) errors.push('Timeout must be greater than 0')
    if (config.stagnationSeconds < 0.1)
      errors.push('Stagnation timeout must be at least 0.1 seconds')
    if (config.dwellSeconds < 0) errors.push('Dwell time must be at least 0 seconds')
    if (deadbandMrad < 1) errors.push('Deadband must be at least 1')
    // Soft-stop window check: target positions outside the soft-stop window
    // get clamped by firmware, so the actuator never reaches the typed
    // value and the test stagnates / errors. Surface this up-front.
    if (softStopMin !== undefined && softStopMax !== undefined) {
      const fmt = (v: number) => v.toFixed(2)
      if (config.position0 < softStopMin || config.position0 > softStopMax) {
        errors.push(
          `Position 0 (${config.position0}) is outside the soft-stop window [${fmt(softStopMin)}, ${fmt(softStopMax)}] ${unitLabel}`
        )
      }
      if (config.position1 < softStopMin || config.position1 > softStopMax) {
        errors.push(
          `Position 1 (${config.position1}) is outside the soft-stop window [${fmt(softStopMin)}, ${fmt(softStopMax)}] ${unitLabel}`
        )
      }
    }
    return errors
  }, [config, deadbandMrad, unitLabel, softStopMin, softStopMax])

  const isValid = validationErrors.length === 0
  const canStart = isValid && (state === CycleTestState.IDLE || state === CycleTestState.COMPLETED)

  const updateConfig = useCallback(
    (key: keyof CycleTestConfig, value: number) => {
      setConfig((prev) => ({ ...prev, [key]: value }))
    },
    [setConfig]
  )

  // Timing config fields, defined outside the JSX so the render only maps over
  // them. `min` doubles as the validation floor: a field is `invalid` when its
  // value drops below `min`, which drives the per-field danger intent.
  const configFields = useMemo(
    () =>
      [
        {
          key: 'cycleCount' as const,
          label: 'Cycle Count',
          value: config.cycleCount,
          min: 1,
          onValueChange: (val: number) => updateConfig('cycleCount', Math.floor(val))
        },
        {
          key: 'timeoutSeconds' as const,
          label: 'Timeout (sec)',
          value: config.timeoutSeconds,
          min: 1,
          onValueChange: (val: number) => updateConfig('timeoutSeconds', val)
        },
        {
          key: 'stagnationSeconds' as const,
          label: 'Stagnation (sec)',
          value: config.stagnationSeconds,
          min: 0.1,
          stepSize: 0.1,
          minorStepSize: 0.01,
          onValueChange: (val: number) => updateConfig('stagnationSeconds', val)
        },
        {
          key: 'dwellSeconds' as const,
          label: 'Dwell (sec)',
          value: config.dwellSeconds,
          min: 0,
          stepSize: 0.1,
          minorStepSize: 0.01,
          onValueChange: (val: number) => updateConfig('dwellSeconds', val)
        }
      ].map((field) => ({ ...field, invalid: field.value < field.min })),
    [config, updateConfig]
  )

  const sendPositionCommand = useCallback(
    (positionUserUnits: number) => {
      if (connectedDevice === undefined) return
      // positionUserUnits is in plain user units (mm/deg/in/rad). The wire
      // format expects user-units × 10^resolution, so scale before sending
      // (same convention as Position.tsx).
      const wireValue = Math.round(positionUserUnits * resolutionFactor)
      hidSend(
        connectedDevice.handle,
        serializeSetUsbThrottleControlRequest({
          value: wireValue,
          motorIndex: actuatorIndex
        }) as BufferSource
      ).catch((e) => console.warn('Failed to send position command:', e))
    },
    [connectedDevice, resolutionFactor, actuatorIndex]
  )

  // Moving state effect
  useEffect(() => {
    if (state !== CycleTestState.MOVING_TO_POS0 && state !== CycleTestState.MOVING_TO_POS1) return
    const target = state === CycleTestState.MOVING_TO_POS0 ? config.position0 : config.position1

    const tick = async () => {
      sendPositionCommand(target)

      if (positionRef.current === undefined) return
      const now = Date.now()

      if (now - moveStartRef.current > config.timeoutSeconds * 1000) {
        setState(CycleTestState.ERROR)
        stateRef.current = CycleTestState.ERROR
        setErrorMessage(`Timeout: Position not reached within ${config.timeoutSeconds} seconds`)
        setErrorType('TIMEOUT')
        return
      }

      const stagnationThreshold = deadbandMrad / 2
      const positionChanged =
        lastPositionRef.current === null ||
        Math.abs(positionRef.current - lastPositionRef.current) > stagnationThreshold
      if (positionChanged) {
        lastPositionRef.current = positionRef.current
        lastPositionChangeTimeRef.current = now
      } else if (now - lastPositionChangeTimeRef.current > config.stagnationSeconds * 1000) {
        setState(CycleTestState.ERROR)
        stateRef.current = CycleTestState.ERROR
        setErrorMessage(`Failure: Position not changing for ${config.stagnationSeconds} seconds`)
        setErrorType('STAGNATION')
        return
      }

      if (Math.abs(positionRef.current - target) < deadbandMrad) {
        dwellStartRef.current = now
        const nextState =
          state === CycleTestState.MOVING_TO_POS0
            ? CycleTestState.WAITING_AT_POS0
            : CycleTestState.WAITING_AT_POS1
        setState(nextState)
        stateRef.current = nextState
      }
    }
    tick()
    const interval = setInterval(tick, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [state, config, deadbandMrad, sendPositionCommand])

  // Waiting state effect
  useEffect(() => {
    if (state !== CycleTestState.WAITING_AT_POS0 && state !== CycleTestState.WAITING_AT_POS1) return

    const tick = async () => {
      const now = Date.now()
      if (now - dwellStartRef.current < config.dwellSeconds * 1000) return

      if (state === CycleTestState.WAITING_AT_POS0) {
        // The MOVING_TO_POS1 effect commands the target (position1) immediately on
        // mount, so there's no send here — issuing one would command the position we
        // just dwelled at (position0), not the new target.
        moveStartRef.current = now
        lastPositionRef.current = null
        lastPositionChangeTimeRef.current = now
        setTargetPosition(config.position1)
        setState(CycleTestState.MOVING_TO_POS1)
        stateRef.current = CycleTestState.MOVING_TO_POS1
      } else {
        // Same as above: the MOVING_TO_POS0 effect commands position0 on mount, so
        // no send here (a send would re-command position1, the dwelled position).
        const newCycleNum = currentCycle + 1
        setCurrentCycle(newCycleNum)
        setCycleLog((prev) => [{ CycleIteration: newCycleNum, completedAt: new Date() }, ...prev])

        if (startTime) {
          setAverageCycleMs((now - startTime.getTime()) / newCycleNum)
        }

        if (newCycleNum >= config.cycleCount) {
          setState(CycleTestState.COMPLETED)
          stateRef.current = CycleTestState.COMPLETED
          try {
            const sendFaultAlert = httpsCallable(firebaseFunctions, 'sendFaultAlert')
            await sendFaultAlert({
              errorType: 'Complete',
              CycleIteration: newCycleNum,
              elapsedMs: startTime ? now - startTime.getTime() : 0,
              exportedFilename: undefined
            })
          } catch (e) {
            console.warn('Completion email notification failed:', e)
          }
        } else {
          moveStartRef.current = now
          lastPositionRef.current = null
          lastPositionChangeTimeRef.current = now
          setTargetPosition(config.position0)
          setState(CycleTestState.MOVING_TO_POS0)
          stateRef.current = CycleTestState.MOVING_TO_POS0
        }
      }
    }
    tick()
    const interval = setInterval(tick, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [state, currentCycle, config, startTime])

  // Auto-export on error
  useEffect(() => {
    const doAutoExport = async () => {
      if (state === CycleTestState.ERROR && !autoExported && errorType) {
        setAutoExported(true)
        try {
          if (!realtimeMetadata.timeWindow) return
          const exportWindow = realtimeMetadata.timeWindow
          const filename = filenameWithTimestamp(
            `leidos-cycle-${actuatorIndex}-${errorType.toLowerCase()}`
          )
          const downloadedFiles = new Set<string>()
          const downloadDataOnce = (fileName: string, data: string | Uint8Array) => {
            if (downloadedFiles.has(fileName)) return
            downloadedFiles.add(fileName)
            downloadData(fileName, data)
            setDownloadedFileName(fileName)
            setShowDownloadedAlert(true)
          }
          await packetThreadPoolApi.export(
            filename,
            ExportType.Playback,
            exportWindow,
            proxy(downloadDataOnce)
          )

          const sendFaultAlert = httpsCallable(firebaseFunctions, 'sendFaultAlert')
          await sendFaultAlert({
            errorType,
            CycleIteration: currentCycle,
            elapsedMs,
            exportedFilename: filename
          })
        } catch (e) {
          console.warn('Auto-export failed:', e)
        }
      }
      if (state !== CycleTestState.ERROR && autoExported) {
        setAutoExported(false)
      }
    }
    doAutoExport()
  }, [state, errorType, autoExported, currentCycle, elapsedMs, actuatorIndex])

  // Elapsed time tracking
  useEffect(() => {
    if (
      state === CycleTestState.IDLE ||
      state === CycleTestState.COMPLETED ||
      state === CycleTestState.ERROR
    )
      return
    const updateElapsed = () => {
      if (startTime) setElapsedMs(Date.now() - startTime.getTime())
    }
    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)
    return () => clearInterval(interval)
  }, [state, startTime])

  // Wake lock
  useEffect(() => {
    const isRunning =
      state !== CycleTestState.IDLE &&
      state !== CycleTestState.COMPLETED &&
      state !== CycleTestState.ERROR
    if (!isRunning) return

    let wakeLock: { release: () => void } | null = null
    const acquire = async () => {
      try {
        wakeLock = await (
          navigator as Navigator & {
            wakeLock: { request: (type: string) => Promise<{ release: () => void }> }
          }
        ).wakeLock.request('screen')
      } catch (e) {
        console.warn('Wake lock not available:', e)
      }
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire()
    }
    acquire()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      wakeLock?.release()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [state])

  const handleStart = useCallback(() => {
    setCurrentCycle(0)
    setCycleLog([])
    setErrorMessage('')
    setStartTime(new Date())
    setElapsedMs(0)
    setTargetPosition(config.position0)
    moveStartRef.current = Date.now()
    lastPositionRef.current = null
    lastPositionChangeTimeRef.current = Date.now()
    setState(CycleTestState.MOVING_TO_POS0)
    stateRef.current = CycleTestState.MOVING_TO_POS0
    setCycleLog([{ CycleIteration: 0, completedAt: new Date() }])
  }, [config.position0])

  const handleStop = useCallback(() => {
    setState(CycleTestState.IDLE)
    stateRef.current = CycleTestState.IDLE
  }, [])

  const handlePause = useCallback(() => {
    previousStateRef.current = stateRef.current
    setState(CycleTestState.PAUSED)
    stateRef.current = CycleTestState.PAUSED
  }, [])

  const handleResume = useCallback(() => {
    const prevState = previousStateRef.current
    setState(prevState)
    stateRef.current = prevState
    if (
      prevState === CycleTestState.MOVING_TO_POS0 ||
      prevState === CycleTestState.MOVING_TO_POS1
    ) {
      moveStartRef.current = Date.now()
      lastPositionRef.current = null
      lastPositionChangeTimeRef.current = Date.now()
    } else if (
      prevState === CycleTestState.WAITING_AT_POS0 ||
      prevState === CycleTestState.WAITING_AT_POS1
    ) {
      // Restart the dwell timer on resume. Otherwise a pause longer than the dwell
      // window leaves dwellStartRef stale, the WAITING tick's guard passes immediately,
      // and the cycle advances without dwelling.
      dwellStartRef.current = Date.now()
    }
  }, [])

  const handleReset = useCallback(() => {
    setState(CycleTestState.IDLE)
    stateRef.current = CycleTestState.IDLE
    setErrorMessage('')
  }, [])

  const isActive =
    state !== CycleTestState.IDLE &&
    state !== CycleTestState.COMPLETED &&
    state !== CycleTestState.ERROR
  const estimatedRemainingMs =
    averageCycleMs > 0 ? (config.cycleCount - currentCycle) * averageCycleMs : 0
  const progress = config.cycleCount > 0 ? currentCycle / config.cycleCount : 0

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const getStateLabel = (s: CycleTestState): string => {
    switch (s) {
      case CycleTestState.IDLE:
        return 'Ready'
      case CycleTestState.MOVING_TO_POS0:
        return 'Moving to Position 0'
      case CycleTestState.WAITING_AT_POS0:
        return `Waiting at Position 0 (${Math.ceil((config.dwellSeconds * 1000 - (Date.now() - dwellStartRef.current)) / 1000)}s)`
      case CycleTestState.MOVING_TO_POS1:
        return 'Moving to Position 1'
      case CycleTestState.WAITING_AT_POS1:
        return `Waiting at Position 1 (${Math.ceil((config.dwellSeconds * 1000 - (Date.now() - dwellStartRef.current)) / 1000)}s)`
      case CycleTestState.PAUSED:
        return 'Paused'
      case CycleTestState.COMPLETED:
        return 'Complete'
      case CycleTestState.ERROR:
        return 'Error'
    }
  }

  const widgetTitle = label ? `Cycle Test — ${label}` : `Cycle Test — Motor ${actuatorIndex}`

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        color: Colors.WHITE,
        padding: 12,
        gap: 6,
        overflow: 'auto'
      }}
    >
      <style>{`
            .ct-input {
                background-color: rgba(255,255,255,0.12) !important;
                color: white !important;
                box-shadow: none !important;
            }
            .ct-input:focus {
                box-shadow: 0 0 0 1px rgba(255,255,255,0.25) !important;
            }
            .bp5-numeric-input .bp5-button-group .bp5-button {
                background-color: rgba(255,255,255,0.12) !important;
                color: rgba(255,255,255,0.7) !important;
                box-shadow: none !important;
                min-height: 14px !important;
            }
            .bp5-numeric-input .bp5-button-group .bp5-button:hover {
                background-color: rgba(255,255,255,0.22) !important;
                color: white !important;
            }
            `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: 6,
          borderBottom: '1px solid rgba(255,255,255,0.12)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon
            icon={faGripVertical}
            color={Colors.WHITE}
            style={{ cursor: 'pointer', opacity: 0.4 }}
          />
          <span style={{ fontWeight: 600, fontSize: 13 }}>{widgetTitle}</span>
        </div>
        <FontAwesomeIcon
          icon={faXmark}
          color={Colors.WHITE}
          style={{ cursor: 'pointer', opacity: 0.6 }}
          onClick={close}
        />
      </div>

      {/* Warnings / Errors */}
      <Callout intent="warning" icon={false} style={{ padding: '4px 8px', fontSize: 11 }}>
        Cycle testing is designed for low-speed devices. High speed operation may cause unexpected
        behavior
      </Callout>

      {state === CycleTestState.ERROR && (
        <Callout intent="danger" icon={false} style={{ padding: '4px 8px', fontSize: 11 }}>
          {errorMessage}
        </Callout>
      )}

      <Alert
        isOpen={showDownloadedAlert}
        onConfirm={() => setShowDownloadedAlert(false)}
        confirmButtonText="OK"
        intent="success"
      >
        <p>
          <b>{errorType.replace(/_/g, ' ')}</b> error occurred.
          <br />
          File downloaded: <b>{downloadedFileName}</b>
        </p>
      </Alert>

      {/* Position config */}
      <div
        style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 4,
          padding: '8px 8px 6px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div>
            <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 2 }}>
              Position 0 ({unitLabel})
            </label>
            <NumericInput
              value={config.position0}
              onValueChange={(val) => updateConfig('position0', val)}
              disabled={isActive}
              fill
              style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: Colors.WHITE }}
              inputClassName="ct-input"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 2 }}>
              Position 1 ({unitLabel})
            </label>
            <NumericInput
              value={config.position1}
              onValueChange={(val) => updateConfig('position1', val)}
              disabled={isActive}
              fill
              style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: Colors.WHITE }}
              inputClassName="ct-input"
            />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 2 }}>
            Deadband ({unitLabel})
          </label>
          <NumericInput
            value={deadbandMrad}
            onValueChange={(val) => setDeadbandMrad(val)}
            min={1}
            stepSize={1}
            disabled={isActive}
            fill
            style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: Colors.WHITE }}
            inputClassName="ct-input"
          />
        </div>
      </div>

      {/* Timing config */}
      <div
        style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 4,
          padding: '8px 8px 6px'
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {configFields.map(
            ({ key, label, value, min, stepSize, minorStepSize, onValueChange, invalid }) => (
              <div key={key}>
                <label style={{ fontSize: 11, opacity: 0.6, display: 'block', marginBottom: 2 }}>
                  {label}
                </label>
                <NumericInput
                  value={value}
                  min={min}
                  stepSize={stepSize}
                  minorStepSize={minorStepSize}
                  onValueChange={onValueChange}
                  clampValueOnBlur
                  intent={invalid ? 'danger' : 'none'}
                  disabled={isActive}
                  fill
                  style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: Colors.WHITE }}
                  inputClassName="ct-input"
                />
              </div>
            )
          )}
        </div>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div
          style={{
            fontSize: 11,
            color: '#ff6b6b',
            backgroundColor: 'rgba(255,107,107,0.08)',
            padding: '4px 6px',
            borderRadius: 3
          }}
        >
          {validationErrors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 6, paddingTop: 2 }}>
        {canStart && (
          <Button intent="success" onClick={handleStart} disabled={!isValid} small>
            Start
          </Button>
        )}
        {isActive && state !== CycleTestState.PAUSED && (
          <Button intent="warning" onClick={handlePause} small>
            Pause
          </Button>
        )}
        {state === CycleTestState.PAUSED && (
          <Button intent="primary" onClick={handleResume} small>
            Resume
          </Button>
        )}
        {isActive && (
          <Button intent="danger" onClick={handleStop} small>
            Stop
          </Button>
        )}
        {state === CycleTestState.ERROR && (
          <Button intent="primary" onClick={handleReset} small>
            Reset
          </Button>
        )}
      </div>

      {/* Status */}
      {(isActive || state === CycleTestState.COMPLETED) && (
        <div
          style={{
            fontSize: 12,
            backgroundColor: 'rgba(255,255,255,0.05)',
            padding: '8px',
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 6
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ opacity: 0.9 }}>
              <strong>Status:</strong> {getStateLabel(state)} — Cycle {currentCycle} of{' '}
              {config.cycleCount}
            </span>
            <span style={{ opacity: 0.6, flexShrink: 0, marginLeft: 8 }}>
              {Math.round(progress * 100)}%
            </span>
          </div>
          <ProgressBar
            value={progress}
            intent={state === CycleTestState.COMPLETED ? 'success' : 'primary'}
            animate={state !== CycleTestState.COMPLETED}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 4,
              fontSize: 11,
              opacity: 0.7,
              paddingTop: 2,
              borderTop: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <span>
              Current: {position} {unitLabel}
            </span>
            <span>
              Target: {targetPosition} {unitLabel}
            </span>
            <span>Elapsed: {formatTime(elapsedMs)}</span>
            <span>Remaining: {formatTime(estimatedRemainingMs)}</span>
          </div>
        </div>
      )}

      {/* Cycle log */}
      {cycleLog.length > 0 && (
        <div
          style={{
            fontSize: 11,
            backgroundColor: 'rgba(255,255,255,0.05)',
            padding: '8px',
            borderRadius: 4
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              opacity: 0.5,
              marginBottom: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Cycle Log
          </div>
          <div style={{ maxHeight: 72, overflowY: 'auto' }}>
            {cycleLog.map((entry, i) => (
              <div key={i} style={{ opacity: i === 0 ? 0.9 : 0.55, paddingBottom: 1 }}>
                {entry.CycleIteration === 0
                  ? `Test started at ${entry.completedAt.toLocaleTimeString()}`
                  : `Cycle ${entry.CycleIteration} completed at ${entry.completedAt.toLocaleTimeString()}`}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
